import moment from "moment";
import { Fragment, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, Link, useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import swal from "@sweetalert/with-react";
import NumberFormat from "react-number-format";
import Card from "../../components/Card";
import Loader from "../../components/Loader";
import TextInput from "../../components/TextInput";
import Button from "../../components/Button";
import BigLoader from "../../components/BigLoader";
import { setInterface } from "../../redux/slices/interfaceSlice";
import service from "../../service";
import "./UserView.css";

const TABS = [
  { key: "info", label: "Info" },
  { key: "access", label: "Access" },
  { key: "orders", label: "Orders" },
  { key: "payments", label: "Payments" },
  { key: "password", label: "Password" },
  { key: "log", label: "Log" },
];

export default function UserView() {
  const { id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("info");

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const {
    register: registerPw,
    handleSubmit: handleSubmitPw,
    reset: resetPw,
    getValues: getValuesPw,
    formState: { errors: errorsPw },
  } = useForm();

  const [pwSaving, setPwSaving] = useState(false);

  const savePassword = ({ new_password }) => {
    setPwSaving(true);
    service
      .put(`/api/admin/users/${id}/password`, { new_password })
      .then(() => {
        setPwSaving(false);
        resetPw();
        swal("Berhasil", "Password user berhasil diubah.", "success");
      })
      .catch((e) => {
        setPwSaving(false);
        swal(
          "Gagal",
          e?.response?.data?.message ||
            (Array.isArray(e?.response?.data) && e.response.data[0]?.message) ||
            "Gagal mengubah password.",
          "error"
        );
      });
  };

  const getData = () => {
    setLoading(true);
    service
      .get(`/api/admin/users/${id}`)
      .then((res) => {
        setData(res.data);
        setLoading(false);
        reset({
          fullname: res.data.fullname || "",
          username: res.data.username || "",
          email: res.data.email || "",
        });
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    dispatch(setInterface({ active: "Users", pageTitle: "Detail User" }));
  }, [dispatch]);

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const revoke = (itemType, itemId, label) => {
    swal({
      title: "Cabut akses?",
      text: `Akses "${label}" akan dicabut dari user ini.`,
      icon: "warning",
      buttons: ["Batal", "Ya, cabut"],
      dangerMode: true,
    }).then((ok) => {
      if (!ok) return;
      setSaving(true);
      service
        .post(`/api/admin/users/${id}/revoke-access`, { item_type: itemType, item_id: itemId })
        .then(() => {
          setSaving(false);
          swal("Berhasil", "Akses dicabut", "success");
          getData();
        })
        .catch((e) => {
          setSaving(false);
          swal("Gagal", e?.response?.data?.error || "Gagal mencabut akses", "error");
        });
    });
  };

  const deletePayment = (paymentId) => {
    swal({
      title: "Hapus payment?",
      text: "Baris payment ini akan dihapus (akses yang sudah ter-grant tidak ikut terhapus).",
      icon: "warning",
      buttons: ["Batal", "Ya, hapus"],
      dangerMode: true,
    }).then((ok) => {
      if (!ok) return;
      setSaving(true);
      service
        .delete(`/api/admin/users/${id}/payments/${paymentId}`)
        .then(() => {
          setSaving(false);
          swal("Berhasil", "Payment dihapus", "success");
          getData();
        })
        .catch((e) => {
          setSaving(false);
          swal("Gagal", e?.response?.data?.error || "Gagal menghapus", "error");
        });
    });
  };

  const deleteOrder = (orderId) => {
    swal({
      title: "Hapus order?",
      text: "Order beserta item dan payment-nya akan dihapus.",
      icon: "warning",
      buttons: ["Batal", "Ya, hapus"],
      dangerMode: true,
    }).then((ok) => {
      if (!ok) return;
      setSaving(true);
      service
        .delete(`/api/admin/users/${id}/orders/${orderId}`)
        .then(() => {
          setSaving(false);
          swal("Berhasil", "Order dihapus", "success");
          getData();
        })
        .catch((e) => {
          setSaving(false);
          swal("Gagal", e?.response?.data?.error || "Gagal menghapus", "error");
        });
    });
  };

  const toggleBan = () => {
    const banning = !data.banned;
    swal({
      title: banning ? "Ban user ini?" : "Unban user ini?",
      text: banning
        ? "User tidak akan bisa login dan sesi aktifnya dicabut."
        : "User akan bisa login lagi.",
      icon: "warning",
      buttons: ["Batal", banning ? "Ya, ban" : "Ya, unban"],
      dangerMode: banning,
    }).then((ok) => {
      if (!ok) return;
      setSaving(true);
      service
        .post(`/api/admin/users/${id}/ban`, { banned: banning })
        .then(() => {
          setSaving(false);
          swal("Berhasil", banning ? "User dibanned" : "User di-unban", "success");
          getData();
        })
        .catch((e) => {
          setSaving(false);
          swal("Gagal", e?.response?.data?.error || "Gagal", "error");
        });
    });
  };

  const deleteUser = () => {
    swal({
      title: "Hapus user ini?",
      text: `User "${data.fullname}" akan dihapus permanen beserta seluruh data transaksinya (orders, payments, akses). Tindakan ini tidak bisa dibatalkan.`,
      icon: "warning",
      buttons: ["Batal", "Ya, hapus permanen"],
      dangerMode: true,
    }).then((ok) => {
      if (!ok) return;
      setSaving(true);
      service
        .delete(`/api/admin/users/${id}`)
        .then(() => {
          setSaving(false);
          swal("Berhasil", "User dan data terkait berhasil dihapus.", "success").then(() =>
            history.push("/users")
          );
        })
        .catch((e) => {
          setSaving(false);
          swal("Gagal", e?.response?.data?.error || "Gagal menghapus user", "error");
        });
    });
  };

  const saveInfo = (values) => {
    setSaving(true);
    service
      .put(`/api/admin/users/${id}`, {
        fullname: values.fullname,
        username: values.username,
        email: values.email,
      })
      .then(() => {
        setSaving(false);
        swal("Berhasil", "Info user disimpan", "success");
        getData();
      })
      .catch((e) => {
        setSaving(false);
        const msg = e?.response?.data?.errors
          ? Object.values(e.response.data.errors).flat().join(", ")
          : e?.response?.data?.message || "Gagal menyimpan";
        swal("Gagal", String(msg), "error");
      });
  };

  if (loading) {
    return (
      <div className="h-64 flex justify-center items-center flex-col">
        <Loader className="bg-black-100" />
        <span className="mt-3 text-black-300 text-sm">Memuat</span>
      </div>
    );
  }

  if (!data) {
    return <div className="text-black-300">User tidak ditemukan.</div>;
  }

  const isLifetime =
    data.subscription_end && moment(data.subscription_end).year() >= 2037;

  return (
    <Fragment>
      <Helmet>
        <title>{data.fullname} — User</title>
      </Helmet>

      <div className="mb-4">
        <Link to="/users" className="text-sm text-black-300 hover:text-black-200">
          ← Kembali ke Users
        </Link>
      </div>

      <Card className="mb-4">
        <div className="flex items-center gap-4">
          <img
            src={data.avatar || "/no-image.png"}
            alt={data.fullname}
            className="w-14 h-14 rounded-full object-cover bg-white-200"
          />
          <div className="flex-1 min-w-0">
            <div className="text-lg font-bold text-black-100 truncate flex items-center gap-2">
              {data.fullname}
              {data.banned && (
                <span className="text-xs font-bold bg-red-100 text-red-600 px-2 py-0.5 rounded">
                  BANNED
                </span>
              )}
            </div>
            <div className="text-sm text-black-300">
              @{data.username} · {data.email}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-black-400">Baring Access</div>
            <div className={"text-sm font-bold " + (data.subscription_end ? "text-green-500" : "text-black-400")}>
              {data.subscription_end ? (isLifetime ? "Lifetime" : moment(data.subscription_end).format("DD/MM/YYYY")) : "Tidak ada"}
            </div>
            <div className="mt-2 flex justify-end gap-2">
              <button
                onClick={toggleBan}
                className={
                  "text-xs font-bold px-3 py-1.5 rounded border transition-colors " +
                  (data.banned
                    ? "border-green-300 text-green-600 hover:bg-green-500 hover:text-white-100"
                    : "border-red-300 text-red-500 hover:bg-red-500 hover:text-white-100")
                }
              >
                {data.banned ? "Unban User" : "Ban User"}
              </button>
              <button
                onClick={deleteUser}
                className="text-xs font-bold px-3 py-1.5 rounded border border-red-300 text-red-500 hover:bg-red-500 hover:text-white-100 transition-colors"
              >
                Delete User
              </button>
            </div>
          </div>
        </div>
      </Card>

      <div className="uv-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={"uv-tab" + (tab === t.key ? " active" : "")}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <Card>
        {tab === "info" && (
          <form onSubmit={handleSubmit(saveInfo)} className="max-w-md">
            <TextInput
              label="Nama Lengkap"
              containerClassName="mb-4"
              message={errors.fullname?.message}
              {...register("fullname", { required: "Tidak boleh kosong" })}
            />
            <TextInput
              label="Username"
              containerClassName="mb-4"
              message={errors.username?.message}
              {...register("username", { required: "Tidak boleh kosong" })}
            />
            <TextInput
              label="Email"
              type="email"
              containerClassName="mb-4"
              message={errors.email?.message}
              {...register("email", { required: "Tidak boleh kosong" })}
            />
            <div className="text-xs text-black-400 mb-4">
              Bergabung: {data.created_at ? moment(data.created_at).format("DD/MM/YYYY HH:mm") : "-"}
              {data.is_mentor ? " · Mentor" : ""}
            </div>
            <Button type="submit" color="blue" className="w-28">
              Simpan
            </Button>
          </form>
        )}

        {tab === "password" && (
          <form onSubmit={handleSubmitPw(savePassword)} className="max-w-md">
            <TextInput
              label="Password Baru"
              type="password"
              containerClassName="mb-4"
              message={errorsPw.new_password?.message}
              {...registerPw("new_password", {
                required: "Tidak boleh kosong",
                minLength: { value: 8, message: "Minimal 8 karakter" },
              })}
            />
            <TextInput
              label="Konfirmasi Password Baru"
              type="password"
              containerClassName="mb-4"
              message={errorsPw.confirm_password?.message}
              {...registerPw("confirm_password", {
                required: "Tidak boleh kosong",
                validate: (value) => {
                  const { new_password } = getValuesPw();
                  return value === new_password || "Konfirmasi password tidak sama";
                },
              })}
            />
            <Button type="submit" color="blue" className="w-36" disabled={pwSaving}>
              {pwSaving ? "Menyimpan..." : "Simpan Password"}
            </Button>
          </form>
        )}

        {tab === "access" && (
          <Fragment>
            <h3 className="uv-section">Online Course</h3>
            {data.access.courses.length === 0 ? (
              <p className="uv-empty">Belum ada akses course.</p>
            ) : (
              <ul className="uv-list">
                {data.access.courses.map((c) => (
                  <li key={c.id}>
                    <span>{c.title}</span>
                    <span className="uv-actions">
                      <span className="uv-meta">aktif sejak {moment(c.created_at).format("DD/MM/YYYY")}</span>
                      <button
                        className="uv-revoke"
                        onClick={() => revoke("course", c.id, c.title)}
                        title="Cabut akses"
                      >
                        Cabut
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <h3 className="uv-section">Deep Read</h3>
            {data.access.deep_reads.length === 0 ? (
              <p className="uv-empty">Belum ada akses deep read.</p>
            ) : (
              <ul className="uv-list">
                {data.access.deep_reads.map((d) => (
                  <li key={d.id}>
                    <span>{d.title}</span>
                    <span className="uv-actions">
                      <span className="uv-meta">aktif sejak {moment(d.created_at).format("DD/MM/YYYY")}</span>
                      <button
                        className="uv-revoke"
                        onClick={() => revoke("deep_read", d.id, d.title)}
                        title="Cabut akses"
                      >
                        Cabut
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            )}

            <h3 className="uv-section">Baring Membership</h3>
            {data.subscription_end ? (
              <ul className="uv-list">
                <li>
                  <span>Baring Membership {isLifetime ? "(Lifetime)" : ""}</span>
                  <span className="uv-actions">
                    <span className="uv-meta">
                      {isLifetime ? "lifetime" : `sampai ${moment(data.subscription_end).format("DD/MM/YYYY")}`}
                    </span>
                    <button
                      className="uv-revoke"
                      onClick={() => revoke("subscription", undefined, "Baring Membership")}
                      title="Cabut akses"
                    >
                      Cabut
                    </button>
                  </span>
                </li>
              </ul>
            ) : (
              <p className="uv-empty">Tidak ada membership aktif.</p>
            )}
          </Fragment>
        )}

        {tab === "orders" && (
          <Fragment>
            {data.orders.length === 0 ? (
              <p className="uv-empty">Belum ada order.</p>
            ) : (
              <ul className="uv-list">
                {data.orders.map((o) => (
                  <li key={o.id}>
                    <span>
                      #{o.id} · {o.items.map((i) => i.title).join(", ")}
                    </span>
                    <span className="uv-actions">
                      <span className="uv-meta">
                        <NumberFormat prefix="Rp" thousandSeparator value={o.total} displayType="text" /> ·{" "}
                        {o.is_paid ? "Lunas" : "Pending"} · {moment(o.created_at).format("DD/MM/YYYY")}
                      </span>
                      <button className="uv-revoke" onClick={() => deleteOrder(o.id)} title="Hapus order">
                        Hapus
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Fragment>
        )}

        {tab === "payments" && (
          <Fragment>
            {data.payments.length === 0 ? (
              <p className="uv-empty">Belum ada payment.</p>
            ) : (
              <ul className="uv-list">
                {data.payments.map((p) => (
                  <li key={p.id}>
                    <span>{p.title || p.invoice_public_id || `Payment #${p.id}`}</span>
                    <span className="uv-actions">
                      <span className="uv-meta">
                        <NumberFormat prefix="Rp" thousandSeparator value={p.amount} displayType="text" /> ·{" "}
                        {p.paysys_id || "-"} · {p.paid_at ? moment(p.paid_at).format("DD/MM/YYYY") : "-"}
                      </span>
                      <button className="uv-revoke" onClick={() => deletePayment(p.id)} title="Hapus payment">
                        Hapus
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Fragment>
        )}

        {tab === "log" && (
          <Fragment>
            {data.logs.length === 0 ? (
              <p className="uv-empty">Belum ada aktivitas login.</p>
            ) : (
              <ul className="uv-list">
                {data.logs.map((l, i) => (
                  <li key={i}>
                    <span>Login</span>
                    <span className="uv-meta">{moment(l.created_at).format("DD/MM/YYYY HH:mm")}</span>
                  </li>
                ))}
              </ul>
            )}
          </Fragment>
        )}
      </Card>

      {saving && <BigLoader />}
    </Fragment>
  );
}
